/**
 * Shared quotation financial calculation logic.
 *
 * Extracted from the inline math that used to live directly inside
 * QuotationAdd.js's useEffects and change handlers (duplicated 3x). These
 * formulas mirror api/src/app/utilities/quotationFinancial.js, which the
 * backend uses to independently recompute and verify the same values.
 */

/**
 * Convert an item's cost/price into the quotation's currency, unless the
 * item's original currency already matches (in which case no conversion
 * is applied).
 * @param {object} item - Must have product_cost_original, product_price_original, product_currency_original
 * @param {string} quotationCurrency
 * @param {number} exchangeRate
 * @returns {{product_cost: number, product_price: number}}
 */
export function applyCurrencyConversion(item, quotationCurrency, exchangeRate) {
  const sameCurrency = item.product_currency_original === quotationCurrency;

  const product_cost = sameCurrency
    ? item.product_cost_original
    : parseFloat((item.product_cost_original * exchangeRate).toFixed(3));

  const product_price = sameCurrency
    ? item.product_price_original
    : parseFloat((item.product_price_original * exchangeRate).toFixed(3));

  return { product_cost, product_price };
}

/**
 * Recalculate subtotal/total/margin for a single item from its current
 * price/cost/qty/period/discount fields. Call applyCurrencyConversion first
 * if product_cost/product_price need to be refreshed from a currency change.
 * @param {object} item - Must have product_price, product_cost, product_qty, contract_long_period, product_discount_price
 * @returns {{product_subtotal: number, product_total: number, product_margin_price: number, product_margin_rate: number}}
 */
export function recalculateItemTotals(item) {
  const product_subtotal = parseFloat(
    (item.product_price * item.product_qty * item.contract_long_period).toFixed(3)
  );
  const product_total = parseFloat((product_subtotal - item.product_discount_price).toFixed(3));
  const product_margin_price = parseFloat(
    (product_total - (item.product_cost * item.product_qty * item.contract_long_period)).toFixed(3)
  );
  const product_margin_rate = parseFloat(
    (product_total > 0 ? (product_margin_price / product_total) * 100 : 0).toFixed(3)
  );

  return { product_subtotal, product_total, product_margin_price, product_margin_rate };
}

/**
 * quotation_subtotal = SUM(item.product_total) - already net of item-level
 * discounts, matches the backend's calculateQuotationTotals.
 * @param {array} items
 * @returns {number}
 */
export function calculateQuotationSubtotal(items) {
  return items.reduce((sum, item) => sum + (item.product_total || 0), 0);
}

/**
 * SUM(cost * qty * period) across all items.
 * @param {array} items
 * @returns {number}
 */
export function calculateQuotationCostTotal(items) {
  return items.reduce((sum, item) => {
    const itemCost = parseFloat(
      (item.product_cost * item.product_qty * item.contract_long_period).toFixed(3)
    );
    return sum + (itemCost || 0);
  }, 0);
}

/**
 * Quotation-level total/margin after applying the top-level quotation
 * discount (separate from any item-level discounts already folded into
 * quotationSubtotal).
 * @param {array} items
 * @param {number} quotationSubtotal
 * @param {number} quotationDiscountPrice
 * @returns {{quotationTotal: number, quotationMarginTotal: number, quotationMarginRate: number}}
 */
export function calculateQuotationTotals(items, quotationSubtotal, quotationDiscountPrice) {
  const quotationTotal = parseFloat((quotationSubtotal - quotationDiscountPrice).toFixed(3));

  const totalMargin = items.reduce((sum, item) => sum + (item.product_margin_price || 0), 0);
  const quotationMarginTotal = parseFloat((totalMargin - quotationDiscountPrice).toFixed(3));

  const quotationMarginRate = parseFloat(
    (quotationTotal > 0 ? (quotationMarginTotal / quotationTotal) * 100 : 0).toFixed(3)
  );

  return { quotationTotal, quotationMarginTotal, quotationMarginRate };
}
